/**
 * Raw XHR transport for uploading files directly to Google Cloud Storage.
 *
 * Why XHR instead of fetch:
 *  - fetch() does not expose upload progress events portably in browsers
 *    (Streams-based request bodies are Chromium-only and require HTTP/2).
 *  - XHR is universally supported and integrates cleanly with progress UIs.
 *  - We must NOT send the Authorization or X-Requested-With headers to GCS;
 *    using XHR directly avoids the dashboard's authenticated axios interceptors.
 */

export class GcsUploadError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "GcsUploadError";
    this.status = status;
  }
}

export interface GcsUploadOptions {
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}

const createAbortError = (): DOMException =>
  new DOMException("Aborted", "AbortError");

const createAbortHandler = (
  xhr: XMLHttpRequest,
  reject: (reason?: unknown) => void,
  abortState: { aborted: boolean },
): (() => void) => {
  return () => {
    abortState.aborted = true;
    try {
      xhr.abort();
    } catch {
      // ignore
    }
    reject(createAbortError());
  };
};

/**
 * Single PUT of the full file body to a GCS signed URL (≤100 MB). Resumable
 * sessions (>100 MB) go through `uploadResumable` instead, in chunks.
 */
const putFile = (
  url: string,
  file: File,
  opts: GcsUploadOptions = {},
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const abortState = { aborted: false };
    const onAbort = createAbortHandler(xhr, reject, abortState);

    if (opts.signal) {
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(Math.round((e.loaded * 100) / e.total));
      }
    };

    xhr.onload = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new GcsUploadError(
            xhr.status,
            `GCS PUT failed with status ${xhr.status}: ${xhr.responseText || "(no body)"}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;
      reject(new GcsUploadError(0, "GCS PUT network error"));
    };

    xhr.open("PUT", url, true);
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream",
    );
    xhr.send(file);
  });
};

/**
 * Single PUT to a GCS signed URL. Used for files ≤100 MB.
 */
export const uploadToSignedUrl = (
  signedUrl: string,
  file: File,
  opts: GcsUploadOptions = {},
): Promise<void> => putFile(signedUrl, file, opts);

export type ResumableOffsetResult =
  | { status: "complete" }
  | { status: "incomplete"; offset: number }
  | { status: "expired" };

/**
 * Asks GCS how many bytes of a resumable session it has durably received, per
 * the GCS resumable-upload protocol: `PUT {sessionUri}` with
 * `Content-Range: bytes *\/{totalSize}` and an empty body.
 *
 *  - `308` + a `Range: bytes=0-N` response header -> resume from `N + 1`.
 *  - `308` with no `Range` header -> nothing received yet, resume from `0`.
 *  - `200`/`201` -> GCS already has every byte.
 *  - any other `4xx` -> the session is gone; the caller must start a new one.
 *
 * A `5xx` or network error rejects with `GcsUploadError` instead of resolving
 * `"expired"` — a flaky server error shouldn't throw away a session that may
 * still be perfectly usable.
 */
export const queryResumableOffset = (
  sessionUri: string,
  totalSize: number,
  opts: Pick<GcsUploadOptions, "signal"> = {},
): Promise<ResumableOffsetResult> => {
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const abortState = { aborted: false };
    const onAbort = createAbortHandler(xhr, reject, abortState);

    if (opts.signal) {
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.onload = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;

      if (xhr.status === 308) {
        const range = xhr.getResponseHeader("Range");
        const match = range?.match(/bytes=0-(\d+)/);
        resolve(
          match
            ? { status: "incomplete", offset: Number(match[1]) + 1 }
            : { status: "incomplete", offset: 0 },
        );
      } else if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: "complete" });
      } else if (xhr.status >= 400 && xhr.status < 500) {
        resolve({ status: "expired" });
      } else {
        reject(
          new GcsUploadError(
            xhr.status,
            `GCS resumable offset query failed with status ${xhr.status}: ${xhr.responseText || "(no body)"}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;
      reject(new GcsUploadError(0, "GCS resumable offset query network error"));
    };

    xhr.open("PUT", sessionUri, true);
    xhr.setRequestHeader("Content-Range", `bytes */${totalSize}`);
    xhr.send();
  });
};

/**
 * Chunk size for resumable sessions, per the backend contract: 50 MiB, a
 * multiple of the 256 KiB GCS requires for every chunk but the last.
 */
export const RESUMABLE_CHUNK_SIZE = 50 * 1024 * 1024;

type ChunkResult =
  { status: "complete" } | { status: "incomplete"; offset: number };

/**
 * PUTs one chunk starting at `start`. GCS answers `308` with the `Range` it
 * has durably stored (which may be less than what was sent) or `200`/`201`
 * once it has the whole object.
 */
const putChunk = (
  sessionUri: string,
  file: File,
  start: number,
  opts: GcsUploadOptions,
): Promise<ChunkResult> => {
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const total = file.size;
    const end = Math.min(start + RESUMABLE_CHUNK_SIZE, total);
    const xhr = new XMLHttpRequest();
    const abortState = { aborted: false };
    const onAbort = createAbortHandler(xhr, reject, abortState);

    if (opts.signal) {
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !opts.onProgress || total === 0) return;
      opts.onProgress(Math.round(((start + e.loaded) * 100) / total));
    };

    xhr.onload = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;
      if (xhr.status === 308) {
        const match = xhr.getResponseHeader("Range")?.match(/bytes=0-(\d+)/);
        resolve({
          status: "incomplete",
          offset: match ? Number(match[1]) + 1 : 0,
        });
      } else if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: "complete" });
      } else {
        reject(
          new GcsUploadError(
            xhr.status,
            `GCS resumable PUT failed with status ${xhr.status}: ${xhr.responseText || "(no body)"}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      if (abortState.aborted) return;
      reject(new GcsUploadError(0, "GCS resumable PUT network error"));
    };

    xhr.open("PUT", sessionUri, true);
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream",
    );
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${end - 1}/${total}`);
    xhr.send(file.slice(start, end));
  });
};

/**
 * Uploads `file` to a GCS resumable session URI from `offset` (0 on the first
 * attempt, the offset `queryResumableOffset` reports when resuming) in
 * sequential `RESUMABLE_CHUNK_SIZE` chunks, so a dropped connection only
 * costs the chunk in flight. Each next chunk starts where GCS says it has
 * stored up to, not where the previous one ended. `onProgress` reports
 * `(bytes stored + bytes sent this chunk) / file.size` across all chunks.
 */
export const uploadResumable = async (
  sessionUri: string,
  file: File,
  offset: number,
  opts: GcsUploadOptions = {},
): Promise<void> => {
  let current = offset;
  for (;;) {
    const result = await putChunk(sessionUri, file, current, opts);
    if (result.status === "complete") return;
    // Sin este corte, una sesión que no avanza dejaría el bucle girando.
    if (result.offset <= current || result.offset >= file.size) {
      throw new GcsUploadError(
        308,
        `GCS resumable upload stalled at byte ${result.offset} of ${file.size}`,
      );
    }
    current = result.offset;
  }
};
